/**
 * Capability gate — decides whether the bootstrap mounts the 3D
 * <Experience /> route or redirects the visitor to <ReadTheTour /> before
 * any WebGL2 context is requested.
 *
 * Called exactly once from src/main.tsx before createRoot().render(). The
 * function is defense-in-depth across three signals:
 *
 *   1. three.js's shipped WebGL.isWebGL2Available() utility — primary signal.
 *   2. navigator.deviceMemory + matchMedia('(hover: hover)') — escalated
 *      soft signal for low-memory touch-only devices (mobile phones with
 *      <2 GB). Desktop Chrome on a 1 GB VM (hover:hover matches) is NOT
 *      redirected; only the low-mem touch-only combination is.
 *   3. canvas.getContext('webgl2') try/catch — guards against the Safari
 *      iOS / GPU-blacklist false-positive documented in
 *      07-RESEARCH.md Pitfall 2 (browser claims support, context
 *      creation fails).
 *
 * Explicitly NOT checked:
 *   - prefers-reduced-motion: this is a motion preference, not a capability
 *     signal. Gating on it would route users who want less motion to the
 *     no-3D fallback, which is user-hostile. (07-RESEARCH.md §Pattern 1
 *     and the Anti-Pattern list.)
 *
 * Import path: three.js r184 ships WebGL.isWebGL2Available at
 * `three/examples/jsm/capabilities/WebGL.js`. The import is module-level but
 * every access to WebGL.isWebGL2Available happens INSIDE the exported
 * functions — never at top-level — so unit tests can vi.mock the module and
 * have the mock active before chooseEntryRoute() evaluates.
 */
import WebGL from 'three/examples/jsm/capabilities/WebGL.js';

export type GateReason = 'no-webgl2' | 'low-mem-touch' | 'context-failure';

export type EntryRoute =
  | { route: 'experience' }
  | { route: 'read'; reason: GateReason };

/**
 * Thin boolean mirror of WebGL.isWebGL2Available() for callers that only
 * need the primary signal (e.g., Experience.tsx could refuse to mount its
 * Canvas if this returns false — though in practice chooseEntryRoute()
 * will have already redirected the visitor to /read before Experience
 * renders).
 *
 * Guarded against API drift: if the utility ever stops exporting the
 * function (three.js refactor, bundler tree-shaking wipes it), this returns
 * false. Also catches any synchronous throw from the underlying
 * `document.createElement('canvas').getContext('webgl2')` call that
 * isWebGL2Available performs internally.
 */
export function isWebGL2Supported(): boolean {
  try {
    return (
      typeof WebGL?.isWebGL2Available === 'function' &&
      WebGL.isWebGL2Available()
    );
  } catch {
    return false;
  }
}

/**
 * Decide which route the visitor should land on.
 *
 * Flow:
 *   1. If no WebGL2 at all -> /read?reason=no-webgl2
 *   2. Else if deviceMemory < 2 AND hover:hover does NOT match
 *      (low-memory touch-only device) -> /read?reason=low-mem-touch
 *   3. Else try to actually create a webgl2 context. If it returns null
 *      or throws -> /read?reason=context-failure
 *   4. Else -> /experience
 *
 * Non-goals: this function does NOT mutate history, does NOT dispatch
 * navigation, does NOT observe React state. It is a pure browser-feature
 * probe returning a decision object. main.tsx owns the history mutation.
 */
export function chooseEntryRoute(): EntryRoute {
  if (!isWebGL2Supported()) {
    return { route: 'read', reason: 'no-webgl2' };
  }

  const mem = (navigator as { deviceMemory?: number }).deviceMemory;
  const hoverCapable = window.matchMedia('(hover: hover)').matches;
  if (typeof mem === 'number' && mem < 2 && !hoverCapable) {
    return { route: 'read', reason: 'low-mem-touch' };
  }

  // Safari iOS / GPU-blacklist false-positive guard
  // (07-RESEARCH.md Pitfall 2).
  try {
    const c = document.createElement('canvas');
    const ctx = c.getContext('webgl2');
    if (!ctx) return { route: 'read', reason: 'context-failure' };
  } catch {
    return { route: 'read', reason: 'context-failure' };
  }

  return { route: 'experience' };
}
